import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'; // Needed for Touch functionality of Material Components
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutModule } from './layout/layout.module';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig } from '@angular/material/snack-bar';
import { AuthenticationService } from './services/base/authentication.service';
import { ApiService } from './services/base/api.service';
import { SnackbarService } from './services/base/snackbar.service';
import { AddTokenInterceptor } from './interceptors/add-token.interceptor';
import { AddAcceptInterceptor } from './interceptors/add-accept.interceptor';
import { AuthorizationService } from './services/base/authorization.service';
import { FormService } from './services/base/form.service';
import { ApiEndpointsService } from './services/base/api-endpoints.service';
import { FiltersService } from './services/base/filters.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { QuillService } from './services/base/quill.service';
import { ReactiveFormsModule } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlDutch } from '../@fury/shared/internationalization/mat-paginator/mat-paginator-intl.dutch';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { ErrorStateMatcher } from '@angular/material/core';
import { MtxNativeDatetimeModule } from '@ng-matero/extensions/core';
import { MtxMomentDatetimeModule } from '@ng-matero/extensions-moment-adapter';
import { LightboxModule } from 'ng-gallery/lightbox';
import { PublicKnowledgeBaseService } from './services/knowledge-base/public-knowledge-base.service';
import { FaqService } from './services/knowledge-base/faq.service';
import { AddSocketIdInterceptor } from './interceptors/add-socket-id.interceptor';
import { SupportContactDetailsService } from './services/knowledge-base/support-contact-details.service';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import { User } from '@sentry/angular-ivy';
import { FuryAlertBoxModule } from '../@fury/shared/alert-box/alert-box.module';
import { LayoutService } from './layout/layout.service';
import { LocalStorageService } from './services/base/local-storage.service';
import { MAT_SELECTSEARCH_DEFAULT_OPTIONS, MatSelectSearchOptions } from 'ngx-mat-select-search';
import { FurySharedDateModule } from '../@fury/fury-shared-date.module';
import { BroadcastService } from './services/base/broadcast.service';

import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import * as moment from 'moment';
import { InstantErrorStateMatcher } from '../@fury/utils/instant-error-state-matcher';
import { environment } from '../environments/environment';
import { version } from '../environments/version';
import { Observable, of } from 'rxjs';
import { UserService } from './services/routering/user.service';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';

registerLocaleData(localeNl);
moment.locale('nl');

@NgModule({
  imports: [
    // Angular Core Module // Don't remove!
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ReactiveFormsModule,

    // Fury Core Modules
    AppRoutingModule,
    FurySharedDateModule.forRoot(),

    // Layout Module (Sidenav, Toolbar, Quickpanel, Content)
    LayoutModule,
    MtxNativeDatetimeModule,
    MtxMomentDatetimeModule,

    LightboxModule.withConfig({
      panelClass: 'fullscreen'
    }),

    ServiceWorkerModule.register('ngsw-worker-custom.js', {
      enabled: true, // environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    QuillModule.forRoot({
      suppressGlobalRegisterWarning: true,
    }),
    FuryAlertBoxModule,

    // Register a Service Worker (optional)
    // ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production })
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        console.log(version + environment.appVersionAddon);
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AuthenticationService],
      multi: true
    },
    {
      provide: ErrorStateMatcher,
      useClass: InstantErrorStateMatcher
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      }
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline'
      } as MatFormFieldDefaultOptions
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'bottom'
      } as MatSnackBarConfig
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddAcceptInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddSocketIdInterceptor,
      multi: true
    },
    {
      provide: MatPaginatorIntl,
      useValue: MatPaginatorIntlDutch()
    },
    {
      provide: MAT_SELECTSEARCH_DEFAULT_OPTIONS,
      useValue: <MatSelectSearchOptions>{
        preventHomeEndKeyPropagation: true,
        clearSearchInput: false,
        noEntriesFoundLabel: 'Geen data',
        placeholderLabel: 'Zoeken ...'
      }
    },
    ApiEndpointsService,
    ApiService,
    AuthenticationService,
    AuthorizationService,
    BroadcastService,
    FiltersService,
    QuillService,
    FormService,

    LayoutService,
    LocalStorageService,
    SnackbarService,
    UserService,
    FaqService,
    SupportContactDetailsService,
    PublicKnowledgeBaseService,
    InfiniteScrollModule,
    QuillService,
  ]
})

export class AppModule {
}

export function initializeApp(
  authenticationService: AuthenticationService,
): () => Observable<any> {
  return (): Observable<boolean> => {
    if (!environment.production) {
      console.log('Auth: check if an account is available');
    }
    const localUser: User = authenticationService.getUserFromLocalStorage();
    if (!environment.production) {
      console.log('Auth: found ', localUser);
    }
    if (localUser) {
      if (!environment.production) {
        console.log('Auth: validate an account');
      }
      return authenticationService.getUserAccount();
    }

    if (!environment.production) {
      console.log('Auth: no accounts available, user is not logged in.');
    }
    return of(null);
  };
}
